package com.evoapps.evoshift.domain.explain

import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.LedgerDayItem
import com.evoapps.evoshift.domain.model.RevenueSource
import kotlin.math.abs

/*
 * SH-152: a day's figures explained (the daily results' table, cards and day pane), from the
 * record the screen shows. Its labour cost is listed person by person when the record carries
 * them (worked out now); a record read back as saved holds only the totals by contract type, so
 * [fresh] (the same day worked out now from the month's shifts and cost snapshots) supplies the
 * people; a saved total that no longer matches them says so (a term of its own) instead of
 * pretending the people add up to it.
 */
object DayExplanations {

    /** The day's net sales target. */
    fun salesTarget(record: DailyRecord): Breakdown =
        Breakdown.of(Figure.SALES_TARGET, Side.PLAN, record.revenue.planNetto, listOf(Term(TermKind.SALES_TARGET, record.revenue.planNetto)))

    /** The day's takings and where they came from; null until they are in. */
    fun takings(record: DailyRecord): Breakdown? {
        val takings = record.revenue.realNetto ?: return null
        val kind = when (record.revenue.source) {
            RevenueSource.CSV -> TermKind.TAKINGS_FILE
            RevenueSource.POS -> TermKind.TAKINGS_TILL
            RevenueSource.MANUAL, null -> TermKind.TAKINGS_TYPED
        }
        return Breakdown.of(Figure.TAKINGS, Side.ACTUAL, takings, listOf(Term(kind, takings)))
    }

    private fun netSales(record: DailyRecord, side: Side): Double =
        if (side == Side.PLAN) record.revenue.planNetto else record.revenue.realNetto ?: 0.0

    private fun salesIncome(record: DailyRecord, side: Side): Double =
        if (side == Side.PLAN) record.revenue.salesIncomePlan else record.revenue.salesIncomeReal

    private fun salesIncomeTerm(record: DailyRecord, side: Side): Term {
        val sales = netSales(record, side)
        val commission = record.revenue.commissionPercent
        return if (commission > 0) Term(TermKind.COMMISSION, salesIncome(record, side), percent = commission * 100, base = sales)
        else Term(TermKind.NET_SALES_KEPT, salesIncome(record, side), base = sales)
    }

    private fun fixedRevenueTerm(record: DailyRecord): Term {
        val days = runCatching { kotlinx.datetime.LocalDate.parse(record.date) }.getOrNull()?.let(::daysInMonth)
        return Term(TermKind.FIXED_REVENUE, record.revenue.fixedRevenue, days = days?.let { 1 }, ofDays = days)
    }

    /** What the day's labour % is measured against: the sales income plus the day's share of the fixed revenue. */
    fun labourBase(record: DailyRecord, side: Side): Breakdown {
        val shown = if (side == Side.PLAN) record.labourBasePlan else record.labourBaseReal ?: 0.0
        return Breakdown.of(Figure.INCOME, side, shown, listOf(salesIncomeTerm(record, side), fixedRevenueTerm(record)))
    }

    /** The day's income: the sales income, the fixed revenue share and the ledger's revenue items of the day. */
    fun income(record: DailyRecord, side: Side, fresh: DailyRecord? = null): Breakdown {
        val shown = if (side == Side.PLAN) record.incomePlan else record.incomeReal
        val items = ledgerItems(record.revenue.otherRevenue, record.costs.otherRevenueItems, fresh?.costs?.otherRevenueItems)
        return Breakdown.of(
            Figure.REVENUE, side, shown,
            listOf(salesIncomeTerm(record, side), fixedRevenueTerm(record), Term(TermKind.REVENUE_ITEMS, record.revenue.otherRevenue, parts = items))
        )
    }

    /**
     * SH-195: the ledger items behind a day's [total] of other costs (or revenue items), item by
     * item: the record's own, else [fresh]'s (the day worked out now) when they add up to it;
     * none (the total alone) otherwise.
     */
    private fun ledgerItems(total: Double, own: List<LedgerDayItem>, fresh: List<LedgerDayItem>?): List<Term> {
        val items = own.ifEmpty { fresh.orEmpty() }
        if (items.isEmpty() || abs(items.sumOf { it.amount } - total) >= Breakdown.MONEY_EPSILON) return emptyList()
        return items.map { Term(TermKind.LEDGER_ITEM, it.amount, name = it.name) }
    }

    /**
     * The day's labour cost of [side] as [record] holds it: person by person from [record] (worked
     * out now) or from [fresh] (the same day worked out now, for a saved record), else by
     * contract type as saved. People without pay set are listed, not counted.
     */
    fun labourCost(record: DailyRecord, side: Side, fresh: DailyRecord? = null): Breakdown {
        val shown = if (side == Side.PLAN) record.laborStats.planned.totalCost else record.laborStats.actual.totalCost
        val source = record.takeIf { it.costs.plannedByPerson.isNotEmpty() || it.costs.actualByPerson.isNotEmpty() } ?: fresh
        val people = source?.costs?.let { if (side == Side.PLAN) it.plannedByPerson else it.actualByPerson }
        val freshTotal = people?.values?.sumOf { it.cost }
        val terms = buildList {
            if (people != null && freshTotal != null && (people.isNotEmpty() || abs(shown) < Breakdown.MONEY_EPSILON)) {
                addAll(MonthExplanations.personTerms(people))
            } else {
                val byContract = if (side == Side.PLAN) record.costs.plannedByContractType else record.costs.actualByContractType
                byContract.entries.sortedBy { it.key.ordinal }.forEach { (type, cost) -> add(Term(TermKind.CONTRACT_TYPE, cost, contract = type)) }
            }
            // SH-193: the day's unassigned shifts in the plan: an estimate at the workplace's rate, or listed only.
            val costs = record.costs
            if (side == Side.PLAN && costs.openShifts > 0) {
                val rate = costs.openCostPerHour
                add(
                    if (rate != null) Term(TermKind.OPEN_SHIFTS, costs.openCost, hours = costs.openHours, shifts = costs.openShifts, rate = rate)
                    else Term(TermKind.OPEN_SHIFTS_NOT_PRICED, 0.0, Sign.NOT_COUNTED, hours = costs.openHours, shifts = costs.openShifts)
                )
            }
            val unpriced = (source ?: record).costs.unpricedByPerson
            unpriced.entries.sortedBy { it.key }.forEach { (id, labour) ->
                add(Term(TermKind.PERSON_WITHOUT_PAY, 0.0, Sign.NOT_COUNTED, personId = id, hours = labour.hours, shifts = labour.shifts))
            }
            // SH-206: people who do not count in the labour figures: listed, not counted.
            (source ?: record).costs.excludedByPerson.entries.sortedBy { it.key }.forEach { (id, labour) ->
                add(Term(TermKind.PERSON_NOT_COUNTED, 0.0, Sign.NOT_COUNTED, personId = id, hours = labour.hours, shifts = labour.shifts))
            }
        }
        return Breakdown.of(Figure.LABOUR_COST, side, shown, terms, residual = TermKind.SAVED_EARLIER)
    }

    /** The day's costs: its labour cost and the ledger's costs dated on it. */
    fun costs(record: DailyRecord, side: Side, fresh: DailyRecord? = null): Breakdown {
        val labour = labourCost(record, side, fresh)
        val shown = (if (side == Side.PLAN) record.costs.plannedLabour else record.costs.actualByContractType.values.sum()) + record.costs.otherCosts
        return Breakdown.of(
            Figure.COSTS, side, shown,
            listOf(
                labour.asTerm(TermKind.LABOUR_COST),
                // SH-195: the day's share of the recurring costs and its one-off costs, item by item.
                Term(TermKind.OTHER_COSTS, record.costs.otherCosts, parts = ledgerItems(record.costs.otherCosts, record.costs.otherCostItems, fresh?.costs?.otherCostItems))
            )
        )
    }

    /** The day's labour % of income (of sales without a commission): labour cost ÷ [labourBase] × 100. */
    fun labourPercent(record: DailyRecord, side: Side, fresh: DailyRecord? = null): Ratio =
        Ratio(Figure.LABOUR_PERCENT, side, labourCost(record, side, fresh), labourBase(record, side))

    /** The day's labour % of net sales. */
    fun labourPercentOfSales(record: DailyRecord, side: Side, fresh: DailyRecord? = null): Ratio {
        val sales = netSales(record, side)
        val kind = if (side == Side.PLAN) TermKind.SALES_TARGET else TermKind.TAKINGS_TYPED
        return Ratio(
            Figure.LABOUR_PERCENT_OF_SALES, side,
            labourCost(record, side, fresh),
            Breakdown.of(Figure.NET_SALES, side, sales, listOf(Term(kind, sales)))
        )
    }

    /** The day's result: its income minus its costs. */
    fun result(record: DailyRecord, side: Side, fresh: DailyRecord? = null): Breakdown {
        val shown = if (side == Side.PLAN) record.resultPlan else record.resultReal
        return Breakdown.of(
            Figure.RESULT, side, shown,
            listOf(income(record, side, fresh).asTerm(TermKind.REVENUE), costs(record, side, fresh).asTerm(TermKind.COSTS, Sign.MINUS))
        )
    }

    /**
     * SH-195: a run of days' result ([days]: the plan all of them, the actual those with takings):
     * their income (sales income, fixed revenue shares, revenue items) minus their costs (labour,
     * the recurring costs' shares and the one-off costs), the sums of the days'.
     */
    fun resultOfDays(days: Collection<DailyRecord>, side: Side): Breakdown {
        val counted = if (side == Side.PLAN) days.toList() else days.filter { it.revenue.realNetto != null }
        val plan = side == Side.PLAN
        val sales = counted.sumOf { if (plan) it.revenue.planNetto else it.revenue.realNetto ?: 0.0 }
        val commission = counted.filter { it.revenue.commissionPercent > 0 }.sumOf { (if (plan) it.revenue.planNetto else it.revenue.realNetto ?: 0.0) * it.revenue.commissionPercent }
        val salesIncome = counted.sumOf { if (plan) it.revenue.salesIncomePlan else it.revenue.salesIncomeReal }
        val income = Breakdown.of(
            Figure.REVENUE, side, counted.sumOf { if (plan) it.incomePlan else it.incomeReal },
            listOf(
                MonthExplanations.salesIncomeTerm(sales, commission, salesIncome),
                Term(TermKind.FIXED_REVENUE, counted.sumOf { it.revenue.fixedRevenue }, days = counted.size),
                Term(TermKind.REVENUE_ITEMS, counted.sumOf { it.revenue.otherRevenue })
            )
        )
        val labour = counted.sumOf { if (plan) it.costs.plannedLabour else it.costs.actualByContractType.values.sum() }
        val items = counted.flatMap { it.costs.otherCostItems }
        val other = counted.sumOf { it.costs.otherCosts }
        val otherTerms = if (items.isNotEmpty() && abs(items.sumOf { it.amount } - other) < Breakdown.MONEY_EPSILON) {
            listOf(
                Term(TermKind.FIXED_COSTS, items.filter { it.recurring }.sumOf { it.amount }, days = counted.size),
                Term(TermKind.ONE_OFF_COSTS, items.filter { !it.recurring }.sumOf { it.amount })
            )
        } else listOf(Term(TermKind.OTHER_COSTS, other))
        val costs = Breakdown.of(Figure.COSTS, side, labour + other, listOf(Term(TermKind.LABOUR_COST, labour)) + otherTerms)
        return Breakdown.of(
            Figure.RESULT, side, income.value - costs.value,
            listOf(income.asTerm(TermKind.REVENUE), costs.asTerm(TermKind.COSTS, Sign.MINUS))
        )
    }

    private fun daysInMonth(date: kotlinx.datetime.LocalDate): Int {
        val first = kotlinx.datetime.LocalDate(date.year, date.month, 1)
        val next = if (date.month == kotlinx.datetime.Month.DECEMBER) kotlinx.datetime.LocalDate(date.year + 1, 1, 1)
            else kotlinx.datetime.LocalDate(date.year, date.month.ordinal + 2, 1)
        return (next.toEpochDays() - first.toEpochDays()).toInt()
    }
}
